import {
  closestCenter, DndContext, DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove, horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, {
  ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Alert, Button, Spinner,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { Link } from 'react-router-dom';
import { hasToolAccess } from '../../../../utils/toolAccessUtils';
import { showToast } from '../../../../ui/utils';
import {
  apiErrorMessage, buildSaveDiff, DiscoverShelfBook, CONTINUE_PLAYING_HASHTAG, fetchPromotionDiscoverShelves,
  isDiffEmpty, newKey, PACIFIC_TIME_ZONE, savePromotionDiscoverShelves, searchBooks, WorkingItem, WorkingShelf,
} from '../../../DiscoverShelves/discoverShelvesApi';
import { CoverDialog } from '../../../DiscoverShelves/CoverDialog';
import { copyActions, ItemCard, ItemCardAction } from '../../../DiscoverShelves/ItemCard';
import { useReportDirty } from '../../hooks/useReportDirty';
import { Promotion } from '../../types/PromotionTypes';

const DISCOVER_SHELVES_TOOL_KEY = 'discover-shelves';

function SortableItem(props: { id: string; disabled: boolean; children: ReactNode }) {
  const { id, disabled, children } = props;
  const {
    attributes, listeners, setNodeRef, transform, transition,
  } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform), transition, flexShrink: 0, cursor: disabled ? undefined : 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

interface DiscoverShelvesEditorProps {
  promotion: Promotion;
  isEditor: boolean;
  onSaved: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

// Places this promotion's book on shelves: at most one item per shelf, dates always from the promotion.
// Only the promotion's own item can be dragged; the other items show where it lands.
export function DiscoverShelvesEditor(props: DiscoverShelvesEditorProps) {
  const {
    promotion, isEditor, onSaved, onDirtyChange,
  } = props;
  const canEdit = isEditor && hasToolAccess(null, DISCOVER_SHELVES_TOOL_KEY, 'editor');
  const hasDates = Boolean(promotion.startTime && promotion.endTime);

  const [original, setOriginal] = useState<WorkingShelf[]>([]);
  const [working, setWorking] = useState<WorkingShelf[]>([]);
  const [book, setBook] = useState<DiscoverShelfBook | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addShelfKey, setAddShelfKey] = useState('');
  const [coverShelfKey, setCoverShelfKey] = useState<string | null>(null);

  const diff = useMemo(() => buildSaveDiff(original, working), [original, working]);
  const isDirty = !isDiffEmpty(diff);
  useReportDirty(isDirty, onDirtyChange);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    fetchPromotionDiscoverShelves(promotion.id)
      .then((shelves) => {
        setOriginal(shelves);
        setWorking(shelves);
      })
      .catch((err) => setLoadError(apiErrorMessage(err, 'Failed to load discover shelves')))
      .finally(() => setLoading(false));
  }, [promotion.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!promotion.storyUuid) {
      setBook(null);
      return;
    }
    searchBooks(promotion.storyUuid, 0)
      .then((result) => setBook(result.books[0] || null))
      .catch(() => setBook(null));
  }, [promotion.storyUuid]);

  const isOwn = (item: { promotion_id: number | null }) => item.promotion_id === promotion.id;
  const shelvesWithItem = working.filter((shelf) => shelf.items.some(isOwn));
  const availableShelves = working.filter((shelf) => (
    shelf.hashtag !== CONTINUE_PLAYING_HASHTAG && !shelf.items.some(isOwn)
  ));
  const shelfOptions = availableShelves.map((shelf) => ({ key: shelf.key, label: shelf.name }));

  const updateShelf = (shelfKey: string, update: (shelf: WorkingShelf) => WorkingShelf) => {
    setWorking((current) => current.map((shelf) => (shelf.key === shelfKey ? update(shelf) : shelf)));
  };

  const addToShelf = () => {
    if (!addShelfKey || !promotion.storyUuid) {
      return;
    }
    updateShelf(addShelfKey, (shelf) => ({
      ...shelf,
      items: [{
        key: newKey(),
        book_uuid: promotion.storyUuid as string,
        start_time: promotion.startTime,
        end_time: promotion.endTime,
        cover_key: null,
        cover_url: null,
        promotion_id: promotion.id,
        promo_identifier: promotion.identifier,
        active: !promotion.disabled && hasDates ? 1 : 0,
        book,
        promotion: {
          id: promotion.id, title: promotion.title, identifier: promotion.identifier, status: promotion.status,
        },
      }, ...shelf.items],
    }));
    setAddShelfKey('');
  };

  // Menu for this promotion's own card; other books on the shelf have no menu here.
  const ownItemActions = (shelfKey: string, item: WorkingItem): ItemCardAction[] => [
    ...(canEdit ? [
      { label: 'Set custom cover', onClick: () => setCoverShelfKey(shelfKey) },
      {
        label: 'Remove custom cover',
        disabled: !item.cover_key,
        onClick: () => updateShelf(shelfKey, (current) => ({
          ...current,
          items: current.items.map((entry) => (isOwn(entry) ? { ...entry, cover_key: null, cover_url: null } : entry)),
        })),
      },
    ] : []),
    ...copyActions(item),
  ];

  const handleDragEnd = (shelfKey: string) => ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      updateShelf(shelfKey, (shelf) => {
        const from = shelf.items.findIndex((item) => item.key === active.id);
        const to = shelf.items.findIndex((item) => item.key === over.id);
        return { ...shelf, items: arrayMove(shelf.items, from, to) };
      });
    }
  };

  const handleSave = () => {
    setSaving(true);
    savePromotionDiscoverShelves(promotion.id, diff)
      .then(() => {
        showToast({ textMessage: 'Discover shelves saved', variant: 'success' });
        load();
        onSaved();
      })
      .catch((err) => showToast({
        textMessage: apiErrorMessage(err, 'Failed to save discover shelves', original),
        variant: 'danger',
        timeout: 10000,
      }))
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <div className="text-center py-3 mt-3"><Spinner animation="border" variant="primary" /></div>;
  }
  if (loadError) {
    return <Alert variant="danger" className="mt-3">{loadError}</Alert>;
  }

  const coverShelf = coverShelfKey ? working.find((shelf) => shelf.key === coverShelfKey) : undefined;
  const coverItem = coverShelf?.items.find(isOwn);

  return (
    <div className="mt-3">
      {!promotion.storyUuid && <Alert variant="warning">Set the promotion&apos;s book before adding it to a shelf.</Alert>}
      {!hasDates && (
        <Alert variant="warning">
          This promotion has no start/end dates, so its shelf items are inactive and not shown in the app.
        </Alert>
      )}
      {!canEdit && (
        <Alert variant="info">Editing needs editor access to both Promotions and Discover Shelves.</Alert>
      )}

      {shelvesWithItem.length === 0 && <div className="text-muted mb-3">This promotion isn&apos;t on any shelf.</div>}

      {shelvesWithItem.map((shelf) => {
        const ownItem = shelf.items.find(isOwn);
        return (
          <div key={shelf.key} className="border rounded p-2 mb-3">
            <div className="d-flex align-items-center mb-2">
              <strong>{shelf.name}</strong>
              {canEdit && ownItem && (
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="ml-auto"
                  onClick={() => updateShelf(shelf.key, (current) => ({
                    ...current,
                    items: current.items.filter((item) => !isOwn(item)),
                  }))}
                >
                  Remove from shelf
                </Button>
              )}
            </div>
            <div className="d-flex pb-2" style={{ gap: 8, overflowX: 'auto', overflowY: 'hidden' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis]}
                onDragEnd={handleDragEnd(shelf.key)}
              >
                <SortableContext items={shelf.items.map((item) => item.key)} strategy={horizontalListSortingStrategy}>
                  {shelf.items.map((item) => (
                    <SortableItem key={item.key} id={item.key} disabled={!canEdit || !isOwn(item)}>
                      <ItemCard
                        item={item}
                        timeZone={PACIFIC_TIME_ZONE}
                        highlighted={isOwn(item)}
                        hideMenu={!isOwn(item)}
                        actions={isOwn(item) ? ownItemActions(shelf.key, item) : undefined}
                      />
                    </SortableItem>
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        );
      })}

      {canEdit && promotion.storyUuid && (
        <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
          {/* Lists every shelf when empty; typing filters by name. Scales to hundreds of shelves. */}
          <div style={{ width: 320 }}>
            <Typeahead
              id="discover-shelves-add-to-shelf"
              size="sm"
              placeholder="Add to shelf… (type to filter)"
              options={shelfOptions}
              labelKey="label"
              minLength={0}
              paginate
              maxResults={50}
              clearButton
              selected={shelfOptions.filter((option) => option.key === addShelfKey)}
              onChange={(selected) => setAddShelfKey((selected[0] as { key: string } | undefined)?.key || '')}
            />
          </div>
          <Button size="sm" disabled={!addShelfKey} onClick={addToShelf}>Add</Button>
        </div>
      )}

      <div className="d-flex align-items-center">
        <Link to={`/tools/discover-shelves?promotion=${promotion.id}`}>Open in Discover Shelves tool</Link>
        {canEdit && (
          <Button className="ml-auto" disabled={!isDirty || saving} onClick={handleSave}>
            {saving && <Spinner as="span" animation="border" size="sm" className="mr-1" />}
            Save
          </Button>
        )}
      </div>

      {coverShelf && coverItem && (
        <CoverDialog
          bookUuid={coverItem.book_uuid}
          onCancel={() => setCoverShelfKey(null)}
          onConfirm={(cover) => {
            updateShelf(coverShelf.key, (current) => ({
              ...current,
              items: current.items.map((item) => (isOwn(item) ? { ...item, cover_key: cover.key, cover_url: cover.url } : item)),
            }));
            setCoverShelfKey(null);
          }}
        />
      )}
    </div>
  );
}
